/**
 * @fileoverview TransactionFilterPanel - Slide-out filter panel for transactions
 *
 * Provides filtering options for the transaction list including:
 * - Who Paid (user dropdown)
 * - Who Created (user dropdown)
 * - Categories (multi-select checkboxes)
 * - Transaction Type (multi-select checkboxes)
 *
 * Mobile-first design with slide-out animation from right edge.
 */

import { useEffect, useRef } from 'react';
import ToggleSwitch from '../shared/ToggleSwitch';
import styles from './TransactionFilterPanel.module.css';

/**
 * TransactionFilterPanel Component
 *
 * Slide-out panel for filtering transactions.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether panel is visible
 * @param {Function} props.onClose - Called when panel should close
 * @param {Object} props.filters - Current filter values
 * @param {Function} props.onFiltersChange - Called when filters update
 * @param {Function} props.onClearAll - Called to clear all filters
 * @param {Array} props.users - Available users [{userKey, userName}]
 * @param {Array} props.categories - Available category names (strings)
 */
function TransactionFilterPanel({
    isOpen,
    onClose,
    filters,
    onFiltersChange,
    onClearAll,
    users,
    categories
}) {
    const panelRef = useRef(null);

    // Handle Escape key to close panel
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    // Focus trap - focus panel when opened
    useEffect(() => {
        if (isOpen && panelRef.current) {
            panelRef.current.focus();
        }
    }, [isOpen]);

    if (!isOpen) return null;

    // Handle Who Paid change
    const handleWhoPaidChange = (e) => {
        const value = e.target.value ? parseInt(e.target.value, 10) : null;
        onFiltersChange({ ...filters, whoPaid: value });
    };

    // Handle Who Created change
    const handleWhoCreatedChange = (e) => {
        const value = e.target.value ? parseInt(e.target.value, 10) : null;
        onFiltersChange({ ...filters, whoCreated: value });
    };

    // Handle category checkbox change
    const handleCategoryChange = (category, checked) => {
        const newCategories = checked
            ? [...filters.categories, category]
            : filters.categories.filter(c => c !== category);
        onFiltersChange({ ...filters, categories: newCategories });
    };

    // Handle transaction type checkbox change
    const handleTypeChange = (type, checked) => {
        const newTypes = checked
            ? [...filters.transactionTypes, type]
            : filters.transactionTypes.filter(t => t !== type);
        onFiltersChange({ ...filters, transactionTypes: newTypes });
    };

    // Remove individual filter (for chips)
    const removeFilter = (filterType, value) => {
        switch (filterType) {
            case 'whoPaid':
                onFiltersChange({ ...filters, whoPaid: null });
                break;
            case 'whoCreated':
                onFiltersChange({ ...filters, whoCreated: null });
                break;
            case 'category':
                onFiltersChange({
                    ...filters,
                    categories: filters.categories.filter(c => c !== value)
                });
                break;
            case 'transactionType':
                onFiltersChange({
                    ...filters,
                    transactionTypes: filters.transactionTypes.filter(t => t !== value)
                });
                break;
            default:
                break;
        }
    };

    // Build active filter chips
    const activeFilters = [];
    if (filters.whoPaid !== null) {
        const user = users.find(u => u.userKey === filters.whoPaid);
        activeFilters.push({
            type: 'whoPaid',
            label: `Paid: ${user?.userName || 'Unknown'}`,
            value: filters.whoPaid
        });
    }
    if (filters.whoCreated !== null) {
        const user = users.find(u => u.userKey === filters.whoCreated);
        activeFilters.push({
            type: 'whoCreated',
            label: `Created: ${user?.userName || 'Unknown'}`,
            value: filters.whoCreated
        });
    }
    filters.categories.forEach(cat => {
        activeFilters.push({
            type: 'category',
            label: cat,
            value: cat
        });
    });
    filters.transactionTypes.forEach(type => {
        activeFilters.push({
            type: 'transactionType',
            label: type,
            value: type
        });
    });

    const transactionTypes = ['Expense', 'Income', 'Transfer', 'Adjustment'];

    // Determine if user filters should be shown (hide for single-user budgets)
    const showUserFilters = users.length > 1;

    return (
        <>
            {/* Dark overlay */}
            <div
                className={styles.overlay}
                onClick={onClose}
                aria-hidden="true"
            />

            {/* Panel */}
            <div
                ref={panelRef}
                className={styles.panel}
                role="dialog"
                aria-label="Filter transactions"
                tabIndex={-1}
            >
                {/* Header */}
                <div className={styles.header}>
                    <h2 className={styles.title}>Filters</h2>
                    <button
                        onClick={onClose}
                        className={styles.closeButton}
                        aria-label="Close filters"
                    >
                        &times;
                    </button>
                </div>

                {/* Body */}
                <div className={styles.body}>
                    {/* Active Filter Chips */}
                    {activeFilters.length > 0 && (
                        <div className={styles.activeFilters}>
                            <span className={styles.activeLabel}>Active filters:</span>
                            <div className={styles.chipContainer}>
                                {activeFilters.map((filter, index) => (
                                    <span key={`${filter.type}-${filter.value}-${index}`} className={styles.chip}>
                                        {filter.label}
                                        <button
                                            onClick={() => removeFilter(filter.type, filter.value)}
                                            className={styles.chipRemove}
                                            aria-label={`Remove ${filter.label} filter`}
                                        >
                                            &times;
                                        </button>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Who Paid Filter */}
                    {showUserFilters && (
                        <div className={styles.filterSection}>
                            <label className={styles.filterLabel}>Who Paid</label>
                            <select
                                value={filters.whoPaid ?? ''}
                                onChange={handleWhoPaidChange}
                                className={styles.select}
                            >
                                <option value="">All users</option>
                                {users.map(user => (
                                    <option key={user.userKey} value={user.userKey}>
                                        {user.userName}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Who Created Filter */}
                    {showUserFilters && (
                        <div className={styles.filterSection}>
                            <label className={styles.filterLabel}>Who Created</label>
                            <select
                                value={filters.whoCreated ?? ''}
                                onChange={handleWhoCreatedChange}
                                className={styles.select}
                            >
                                <option value="">All users</option>
                                {users.map(user => (
                                    <option key={user.userKey} value={user.userKey}>
                                        {user.userName}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Categories Filter */}
                    {categories.length > 0 && (
                        <div className={styles.filterSection}>
                            <label className={styles.filterLabel}>Categories</label>
                            <div className={styles.toggleList}>
                                {categories.map(category => (
                                    <div key={category} className={styles.toggleItem}>
                                        <ToggleSwitch
                                            checked={filters.categories.includes(category)}
                                            onChange={(checked) => handleCategoryChange(category, checked)}
                                            label={category}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Transaction Type Filter */}
                    <div className={styles.filterSection}>
                        <label className={styles.filterLabel}>Transaction Type</label>
                        <div className={styles.toggleGrid}>
                            {transactionTypes.map(type => (
                                <div key={type} className={styles.toggleItem}>
                                    <ToggleSwitch
                                        checked={filters.transactionTypes.includes(type)}
                                        onChange={(checked) => handleTypeChange(type, checked)}
                                        label={type}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className={styles.footer}>
                    <button
                        onClick={onClearAll}
                        className={styles.clearButton}
                        disabled={activeFilters.length === 0}
                    >
                        Clear All
                    </button>
                    <button
                        onClick={onClose}
                        className={styles.applyButton}
                    >
                        Apply Filters
                    </button>
                </div>
            </div>
        </>
    );
}

export default TransactionFilterPanel;
